import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { ToastsService } from '@catch/services';
import { Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { PlatformTfaMethodType } from '../enums/platform-tfa-method-type.enum';

export interface PlatformTfaMethod {
  id: number;
  name: string;
  created: string;
  lastUsed: string;
  type: PlatformTfaMethodType | string;
}

export interface PlatformTfaSetup {
  id: number;
  code: string;
  'qr-code-image': string;
}

@Injectable({
  providedIn: 'root',
})
export class PlatformTfaService {
  private readonly http = inject(HttpClient);
  private readonly toastsService = inject(ToastsService);
  private readonly base = `${environment.apiBaseUrl}/candidate/platform`;

  readonly methods = signal<PlatformTfaMethod[]>([]);

  /**
   * Loads verified 2FA methods of the logged-in candidate.
   * @returns Observable of the methods
   */
  loadMethods(): Observable<PlatformTfaMethod[]> {
    return this.http
      .get<PlatformTfaMethod[]>(`${this.base}/settings/authentication-methods`)
      .pipe(tap((methods) => this.methods.set(methods)));
  }

  /**
   * Starts authenticator-app setup.
   * @param name - Display name of the method
   * @returns Observable with QR payload
   */
  getAppCredential(name: string): Observable<PlatformTfaSetup> {
    return this.http.post<PlatformTfaSetup>(`${this.base}/settings/2fa-credential`, {
      name,
    });
  }

  /**
   * Confirms authenticator-app setup with a TOTP code.
   * @param id - Credential id
   * @param code - TOTP code
   * @returns Observable of the confirmed name
   */
  confirmAppCredential(id: number, code: string): Observable<{ name: string }> {
    return this.http
      .post<{ name: string }>(`${this.base}/settings/2fa-credential/${id}/confirm`, {
        code,
      })
      .pipe(
        tap((result) => {
          this.methods.update((methods) => [
            ...methods,
            {
              id,
              name: result.name,
              created: new Date().toISOString(),
              lastUsed: new Date().toISOString(),
              type: PlatformTfaMethodType.APP,
            },
          ]);
          this.toastsService.success('jobboard.platform.settings.tfa.create-success');
        })
      );
  }

  /**
   * Deletes an unfinished setup credential without a toast.
   * @param id - Credential id
   */
  cancelAppCreation(id: number): void {
    if (!id) {
      return;
    }
    this.deleteMethod(
      {
        id,
        type: PlatformTfaMethodType.APP,
        created: '',
        lastUsed: '',
        name: '',
      },
      true
    ).subscribe();
  }

  /**
   * Deletes a 2FA method.
   * @param method - Method to remove
   * @param suppressToast - Skip the success toast
   * @returns Observable that completes when deleted
   */
  deleteMethod(method: PlatformTfaMethod, suppressToast = false): Observable<void> {
    return this.http.delete<void>(`${this.base}/settings/credential/${method.id}`).pipe(
      tap(() => {
        this.methods.update((methods) =>
          methods.filter((item) => item.id !== method.id)
        );
        if (method.type === PlatformTfaMethodType.APP && !suppressToast) {
          this.toastsService.success('jobboard.platform.settings.tfa.delete-success');
        }
      })
    );
  }
}
